import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaSpinner, FaPlus } from 'react-icons/fa';
import { getLanguageByCode, updateLanguageTemplate } from '../components/utils/languageApi';
import pb from '../lib/pocketbase.js';
/** @typedef {{ id?: string, name?: string, json_template?: any }} LanguageRecord */
pb.autoCancellation(false);
const LangJson = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  // Removed unused isAuthenticated to avoid linter typing issues
  // const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated ?? false);
  /** @type {[LanguageRecord, React.Dispatch<React.SetStateAction<LanguageRecord>>]} */
  const [languageData, setLanguageData] = useState(/** @type {LanguageRecord} */({}));
  const [jsonData, setJsonData] = useState({});
  const [englishJson, setEnglishJson] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showUntranslatedOnly, setShowUntranslatedOnly] = useState(false);
  const [newKeyword, setNewKeyword] = useState({ key: '', value: '' });
  const [originalJsonData, setOriginalJsonData] = useState({});

  // Toast component
  const Toast = ({ message, type }) => {
    if (!message) return null;
    
    return (
      <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 transform transition-all duration-300 ${
        type === 'success' ? 'bg-[#A1E744]' : 'bg-red-500'
      }`}>
        {message}
      </div>
    );
  };

  // Function to show toast
  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Helper to safely format any value for display
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'string' && val.trim() === '') return '-';
    return typeof val === 'object' ? JSON.stringify(val) : String(val);
  };

  // Check authentication
  useEffect(() => {
    const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
    if (!pbAuth?.record) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const fetchLanguageData = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch current language and English in parallel
        const [languageResponse, englishResponse] = await Promise.all([
          getLanguageByCode(code),
          getLanguageByCode('en')
        ]);
        
        if (!languageResponse) {
          setError('Language not found');
          setLoading(false);
          return;
        }

        setLanguageData(languageResponse || {});

        // Parse current language JSON template
        let parsedJson = {};
        if (languageResponse?.json_template) {
          try {
            parsedJson = typeof languageResponse.json_template === 'string' 
              ? JSON.parse(languageResponse.json_template) 
              : languageResponse.json_template;
          } catch (parseError) {
            console.error('Error parsing JSON template:', parseError);
            setError('Invalid JSON template format');
            setLoading(false);
            return;
          }
        }

        // Parse English JSON template
        let parsedEn = {};
        if (englishResponse?.json_template) {
          try {
            parsedEn = typeof englishResponse.json_template === 'string'
              ? JSON.parse(englishResponse.json_template)
              : englishResponse.json_template;
          } catch (parseError) {
            console.error('Error parsing English JSON template:', parseError);
            // Do not block UI if English parse fails; just keep it empty
            parsedEn = {};
          }
        }

        setEnglishJson(parsedEn || {});

        // Compare with English and add missing keys
        // This comparison happens for all languages except English itself
        if (code !== 'en' && Object.keys(parsedEn).length > 0) {
          let hasUpdates = false;
          const updatedJson = { ...parsedJson };
          const missingKeys = [];
          
          // Add missing keys from English with empty values
          Object.keys(parsedEn).forEach((key) => {
            if (!(key in updatedJson)) {
              updatedJson[key] = '';
              hasUpdates = true;
              missingKeys.push(key);
            }
          });

          // If there are updates, save to database and refresh
          if (hasUpdates && languageResponse?.id) {
            try {
              console.log(`Adding ${missingKeys.length} missing key(s) to language ${code}:`, missingKeys);
              
              // Update the database with the missing keys
              await updateLanguageTemplate(languageResponse.id, updatedJson);
              
              // Refresh language data from database to ensure we have the latest version
              const refreshedResponse = await getLanguageByCode(code);
              setLanguageData(refreshedResponse || languageResponse);
              
              // Update the parsed JSON from refreshed response to ensure consistency
              let refreshedParsedJson = updatedJson;
              if (refreshedResponse?.json_template) {
                try {
                  refreshedParsedJson = typeof refreshedResponse.json_template === 'string' 
                    ? JSON.parse(refreshedResponse.json_template) 
                    : refreshedResponse.json_template;
                } catch (e) {
                  console.warn('Could not parse refreshed response, using updatedJson');
                }
              }
              
              setJsonData(refreshedParsedJson);
              setOriginalJsonData(refreshedParsedJson);
              
              const message = missingKeys.length === 1 
                ? `Added 1 missing key from English template` 
                : `Added ${missingKeys.length} missing keys from English template`;
              showToast(message, 'success');
            } catch (updateError) {
              console.error('Error updating missing keys in database:', updateError);
              showToast('Failed to update database with missing keys', 'error');
              // Still set the data in UI even if database update fails
              setJsonData(updatedJson);
              setOriginalJsonData(updatedJson);
            }
          } else {
            // No updates needed, set the data as is
            setJsonData(parsedJson || {});
            setOriginalJsonData(parsedJson || {});
          }
        } else {
          // For English language or if English template is empty, just set the data
          setJsonData(parsedJson || {});
          setOriginalJsonData(parsedJson || {});
        }

      } catch (error) {
        console.error('Error fetching language data:', error);
        if (error?.status === 404) {
          setError('Language not found');
        } else {
          setError('Failed to fetch language data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLanguageData();
  }, [code]);

  const handleInputChange = (key, value) => {
    setJsonData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const isValueUntranslated = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  };

  const isEnglishMissing = (key) => {
    const englishValue = englishJson?.[key];
    return isValueUntranslated(englishValue);
  };

  const normalizeForSearch = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.toLowerCase();
    try {
      return JSON.stringify(value).toLowerCase();
    } catch {
      return '';
    }
  };

  const matchesSearch = (key, value) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;

    const currentValue = normalizeForSearch(value);
    const englishValue = normalizeForSearch(englishJson?.[key]);
    return (
      key.toLowerCase().includes(term) ||
      currentValue.includes(term) ||
      englishValue.includes(term)
    );
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      // Update the language record with the new JSON template
      const recordId = languageData?.id;
      if (recordId) {
        await updateLanguageTemplate(recordId, jsonData);
      }

      showToast('Language JSON updated successfully!', 'success');
      
      // Refresh the data
      const updatedResponse = await getLanguageByCode(code);
      setLanguageData((updatedResponse || {}) /** @type {LanguageRecord} */);
      
      // Update original data to reflect saved state
      if (updatedResponse?.json_template) {
        try {
          const parsedUpdated = typeof updatedResponse.json_template === 'string' 
            ? JSON.parse(updatedResponse.json_template) 
            : updatedResponse.json_template;
          setOriginalJsonData(parsedUpdated || {});
        } catch (e) {
          // If parse fails, use current jsonData
          setOriginalJsonData(jsonData);
        }
      } else {
        setOriginalJsonData(jsonData);
      }
      
    } catch (error) {
      console.error('Error updating language JSON:', error);
      showToast('Failed to update language JSON', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleAddNewKeyword = () => {
    if (!newKeyword.key.trim()) {
      showToast('Please enter a keyword', 'error');
      return;
    }

    // Check if key already exists
    if (jsonData.hasOwnProperty(newKeyword.key)) {
      showToast('This keyword already exists', 'error');
      return;
    }

    // Add the new keyword
    setJsonData(prev => ({
      ...prev,
      [newKeyword.key]: newKeyword.value
    }));

    // If the current language is English, also add to englishJson (as standard name)
    if (code === 'en') {
      setEnglishJson(prev => ({
        ...prev,
        [newKeyword.key]: newKeyword.value
      }));
    }

    // Clear the input
    setNewKeyword({ key: '', value: '' });
    showToast('New keyword added. Don\'t forget to save!', 'success');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-[#A1E744] mx-auto mb-4" />
          <p className="text-gray-600">Loading language data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl text-gray-400 mb-4">404</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Language Not Found</h1>
          <p className="text-gray-600 mb-6">The language code "{code}" could not be found in our database.</p>
          <button
            onClick={handleBack}
            className="bg-[#A1E744] text-white px-6 py-3 rounded-lg hover:bg-[#8BCF3A] transition-colors duration-200 flex items-center mx-auto"
          >
            <FaArrowLeft className="mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const jsonEntries = Object.entries(jsonData);
  const filteredEntries = jsonEntries.filter(([key, value]) => {
    // If showing untranslated only, check if it was originally untranslated
    // This prevents entries from disappearing when user starts typing
    if (showUntranslatedOnly) {
      const wasOriginallyUntranslated = isValueUntranslated(originalJsonData[key]);
      const isCurrentlyUntranslated = isValueUntranslated(value);
      const isEnglishMissingForKey = isEnglishMissing(key);
      
      // Show if: it was originally untranslated OR it's currently untranslated OR English is missing
      // This keeps entries visible while being edited
      if (!wasOriginallyUntranslated && !isCurrentlyUntranslated && !isEnglishMissingForKey) {
      return false;
      }
    }
    return matchesSearch(key, value);
  });

  return (
    <div className="min-h-screen bg-[#f5f6fa] p-4 md:p-6 lg:p-8">
      <div className="w-[90%] mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-8">
        <div>
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800 transition-colors duration-200 flex items-center mb-2"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            Language: {formatValue(languageData?.name || code)}
          </h1>
          <p className="text-gray-600 mt-2">
            Edit JSON template for language code: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{code}</span>
          </p>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search labels..."
            className="w-full md:w-72 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A1E744] focus:border-transparent"
          />
          <button
            onClick={() => setShowUntranslatedOnly((prev) => !prev)}
            className={`w-full md:w-72 px-3 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center ${
              showUntranslatedOnly
                ? 'bg-[#8BCF3A] text-white hover:bg-[#79BB2F]'
                : 'bg-[#A1E744] text-white hover:bg-[#8BCF3A]'
            }`}
          >
            {showUntranslatedOnly ? 'Show all labels' : 'Show labels that need translation'}
          </button>
        </div>
      </div>

      {/* JSON Editor Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {showUntranslatedOnly && (
          <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-100 text-sm text-yellow-800">
            Showing {filteredEntries.length} label{filteredEntries.length === 1 ? '' : 's'} that still need translation.
          </div>
        )}
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b">
                  Key
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b">
                  Standard Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEntries.length === 0 && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-500 text-sm" colSpan={3}>
                    No labels match your search criteria.
                  </td>
                </tr>
              )}
              {filteredEntries.map(([key, value]) => (
                <tr key={key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-mono text-gray-900 bg-gray-50 break-words">
                    {key}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 break-words">
                    {formatValue(englishJson?.[key])}
                  </td>
                  <td className="px-6 py-4">
                    {value !== null && typeof value === 'object' ? (
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md font-mono text-sm whitespace-pre-wrap text-gray-700">
                        {formatValue(value)}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={value ?? ''}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A1E744] focus:border-transparent"
                        placeholder="-"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#A1E744] text-white px-8 py-3 rounded-lg hover:bg-[#8BCF3A] transition-colors duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <FaSave className="mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Add New Keyword Section - Always Visible */}
      <div className="mt-8 bg-white rounded-lg shadow-lg p-6 border-2 border-dashed border-[#A1E744]">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <FaPlus className="mr-2 text-[#A1E744]" />
          Add New Keyword
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Add a new keyword with its value to the language template.
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keyword (Key)
            </label>
            <input
              type="text"
              value={newKeyword.key}
              onChange={(e) => setNewKeyword(prev => ({ ...prev, key: e.target.value }))}
              placeholder="Enter keyword (e.g., welcome_message)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A1E744] focus:border-transparent"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddNewKeyword();
                }
              }}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Value
            </label>
            <input
              type="text"
              value={newKeyword.value}
              onChange={(e) => setNewKeyword(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Enter value for the keyword"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A1E744] focus:border-transparent"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddNewKeyword();
                }
              }}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleAddNewKeyword}
              className="bg-[#A1E744] text-white px-6 py-2 rounded-lg hover:bg-[#8BCF3A] transition-colors duration-200 flex items-center whitespace-nowrap"
            >
              <FaPlus className="mr-2" />
              Add Keyword
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      <Toast message={toast.message} type={toast.type} />
      </div>
    </div>
  );
};

export default LangJson; 